/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { isJiraProduct } from '../../../../common/utils/product';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import { SearchResult } from '../search-result';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import { SearchResultsListDefaultRenderer } from '../search-results-list-default-renderer';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import type { SearchResultsListRendererProps } from '../types';

import { SearchResultsLiteIssueTable } from './search-results-lite-issue-table';
import { SearchResultsLiteProjectTable } from './search-results-lite-project-table';

const styles = cssMap({
	liteResultsWrapper: {
		display: 'flex',
		flexDirection: 'column',
		// Total height = viewport minus top nav (56px) and filter bar (71px)
		// (need to set an explicit height here so that table containers respect max height styles)
		// @ts-expect-error - calc() not in cssMap's type union
		height: `calc(100vh - 127px)`,
		gap: token('space.300'),
		width: '100%',
	},
	invisibleBottomPadding: {
		flexShrink: 0,
		display: 'block',
		height: '1px',
		width: '1px',
	}
});

const JIRA_ISSUE_TYPENAME = 'SearchResultJiraIssue';
const JIRA_PROJECT_TYPENAME = 'SearchResultJiraProject';
const MAX_ROWS = 50;

export const SearchResultsListLiteRenderer = ({
	containsSearchResults,
	selectedProduct,
	edgesToDisplay,
	thirdPartyConfigs,
	availableProducts,
	originalQuery,
	logError,
	is3pGroupingEnabled,
	smartAnswersPortalRef,
	mainContentWidth,
	cloudId,
	appMode,
	searchQuery,
	primaryProduct,
	searchSessionId,
	linkedPRs,
	teamNames,
	issueStatuses,
	wasContentClearedBySpaceChange,
	clearFilters,
	clearSpaceFilter,
	fetchNextPage,
	cursor,
	isLoadingNext,
	getSelectedFilters,
}: SearchResultsListRendererProps): React.JSX.Element => {
	const jiraIssues = useMemo(
		() => edgesToDisplay.filter((edge) => edge?.node?.__typename === JIRA_ISSUE_TYPENAME),
		[edgesToDisplay],
	);
	const jiraIssuesPruned = jiraIssues.slice(0, MAX_ROWS);

	const jiraProjectsOrSpaces = useMemo(
		() => edgesToDisplay.filter((edge) => edge?.node?.__typename === JIRA_PROJECT_TYPENAME),
		[edgesToDisplay],
	);
	const jiraProjectsOrSpacesPruned = jiraProjectsOrSpaces.slice(0, MAX_ROWS);

	// Determine if we should render the new "lite" search results experience based on the following:
	//   1. the experiment is enabled (already verified by the parent component /search-results/search-results.tsx)
	// 	 2. a Jira product should be selected in the app filter
	//   3. there are results to show
	//   4. the results contain either Jira issues, projects, or spaces
	const shouldRenderLiteList = useMemo(() => {
		const jiraProductIsSelected = selectedProduct && isJiraProduct(selectedProduct, true);
		return jiraProductIsSelected && containsSearchResults;
	}, [containsSearchResults, selectedProduct]);

	return shouldRenderLiteList ? (
		<Box xcss={styles.liteResultsWrapper}>
			{!!jiraIssues.length && 
				<SearchResultsLiteIssueTable shownCount={jiraIssuesPruned.length} totalCount={jiraIssues.length}>
					{jiraIssuesPruned.map(
						(edge, position) =>
							edge?.node && (
								<SearchResult
									key={edge.node.id}
									node={edge.node}
									position={position}
									cloudId={cloudId}
									appMode={appMode}
									searchQuery={searchQuery}
									thirdPartyConfigs={thirdPartyConfigs}
									selectedProduct={selectedProduct}
									primaryProduct={primaryProduct}
									searchSessionId={searchSessionId}
									originalQuery={originalQuery}
									availableProducts={availableProducts}
									logError={logError}
									compassTeamName={teamNames[edge.node.id]}
									jiraIssueStatus={issueStatuses[edge.node.id]}
									jiraLinkedPRs={linkedPRs[edge.node.id]}
									is3pGroupingEnabled={is3pGroupingEnabled}
									smartAnswersPortalRef={smartAnswersPortalRef}
									mainContentWidth={mainContentWidth}
									// Single source of truth for lite-mode rendering: only set
									// inside this lite-list code path (a Jira product is selected
									// and there are results), so the lite table row cannot leak
									// onto the "All" tab via the default list renderer.
									isLiteMode
								/>
							),
					)
				}
				</SearchResultsLiteIssueTable>
			}

			{!!jiraProjectsOrSpaces.length &&
				<SearchResultsLiteProjectTable shownCount={jiraProjectsOrSpacesPruned.length} totalCount={jiraProjectsOrSpaces.length}>
					{jiraProjectsOrSpacesPruned.map(
						(edge, position) =>
							edge?.node && (
								<SearchResult
									key={edge.node.id}
									node={edge.node}
									position={position}
									cloudId={cloudId}
									appMode={appMode}
									searchQuery={searchQuery}
									thirdPartyConfigs={thirdPartyConfigs}
									selectedProduct={selectedProduct}
									primaryProduct={primaryProduct}
									searchSessionId={searchSessionId}
									originalQuery={originalQuery}
									availableProducts={availableProducts}
									logError={logError}
									compassTeamName={teamNames[edge.node.id]}
									jiraIssueStatus={issueStatuses[edge.node.id]}
									jiraLinkedPRs={linkedPRs[edge.node.id]}
									is3pGroupingEnabled={is3pGroupingEnabled}
									smartAnswersPortalRef={smartAnswersPortalRef}
									mainContentWidth={mainContentWidth}
									// See note above on the issues table for why this is the
									// single source of truth for lite-mode rendering.
									isLiteMode
								/>
							),
					)}
				</SearchResultsLiteProjectTable>
			}
			{/* 
				Due to margin collapsing and the way overflows are handled in the HTML markup we add an invisible element here 
				for padding at the bottom of the page. This element is purely for visual purposes and is hidden from a11y technologies.
			*/}
			<div css={styles.invisibleBottomPadding} aria-hidden='true'></div>
		</Box>
	) : (
		<SearchResultsListDefaultRenderer
			containsSearchResults={containsSearchResults}
			edgesToDisplay={edgesToDisplay}
			cloudId={cloudId}
			appMode={appMode}
			searchQuery={searchQuery}
			thirdPartyConfigs={thirdPartyConfigs}
			selectedProduct={selectedProduct}
			primaryProduct={primaryProduct}
			searchSessionId={searchSessionId}
			originalQuery={originalQuery}
			availableProducts={availableProducts}
			logError={logError}
			teamNames={teamNames}
			issueStatuses={issueStatuses}
			linkedPRs={linkedPRs}
			is3pGroupingEnabled={is3pGroupingEnabled}
			smartAnswersPortalRef={smartAnswersPortalRef}
			mainContentWidth={mainContentWidth}
			wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
			clearSpaceFilter={clearSpaceFilter}
			clearFilters={clearFilters}
			getSelectedFilters={getSelectedFilters}
			cursor={cursor}
			isLoadingNext={isLoadingNext}
			fetchNextPage={fetchNextPage}
		/>
	);
};
